import axios from 'axios';
import { isPlanLimitError, planLimitInfoFromError, showPlanLimitModal } from './planLimitModal';
// Client error reporter (2026-09-22, Jaime): API failures with user-visible
// errors are registered server-side. Fire-and-forget; 401/403/413 flows skipped.
import { reportApiFailure } from '../lib/clientErrorReporter';
import { beaconStateAnomaly } from '../lib/homeBeacons';
// TH1 diagnostic lane (2026-09-30, Kimi spec): selection-trace for /home/* requests.
import { traceSel } from '../utils/selTrace';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3002/api';

const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
  // iOS session fix (2026-09-18): without a timeout, requests resumed from
  // background on iOS could hang indefinitely; fail fast instead.
  timeout: 30000,
});

// 429 exponential backoff (2026-09-30, Phase-1 429-incident fix). A shared
// module-level gate: any 429 response arms a pause (respecting Retry-After,
// doubling per consecutive 429, capped at 5 min). Polling loops consult
// is429Backoff() before firing their next tick so a rate-limited session
// stops burning the shared limiter bucket instead of hammering it every
// 12s. No request is ever auto-retried on 429 — the gate only DELAYS
// background polling; user-triggered calls still go through immediately.
// Measured basis: 215x429 in the 2026-09-30 19:40-20:01 nginx window, one
// session, bursts to 83 req/min from /quejas 12s silent-refresh fan-out.
let _backoffUntil = 0;
let _backoffMs = 0;
const BACKOFF_BASE_MS = 15000;
const BACKOFF_MAX_MS = 5 * 60 * 1000;
export function note429(retryAfterHeader?: string | null) {
  let waitMs = _backoffMs > 0 ? Math.min(_backoffMs * 2, BACKOFF_MAX_MS) : BACKOFF_BASE_MS;
  if (retryAfterHeader) {
    const s = Number(retryAfterHeader);
    if (Number.isFinite(s) && s > 0) waitMs = Math.max(waitMs, Math.min(s * 1000, BACKOFF_MAX_MS));
  }
  _backoffMs = waitMs;
  _backoffUntil = Date.now() + waitMs;
}
export function is429Backoff(): boolean { return Date.now() < _backoffUntil; }
export function backoffMsLeft(): number { return Math.max(0, _backoffUntil - Date.now()); }
export function reset429Backoff() { _backoffUntil = 0; _backoffMs = 0; }

// TH1 stale-id lane (2026-10-01): known-unit snapshot written by AuthContext
// refreshMe (owned + membership unit ids). Returns null while the snapshot is
// absent/unparseable — the stale-unit guard must not fire while units load.
const KNOWN_UNIT_IDS_KEY = 'vecindus_known_unit_ids';
function readKnownUnitIds(): Set<string> | null {
  try {
    const raw = localStorage.getItem(KNOWN_UNIT_IDS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    return new Set(parsed.map(String));
  } catch {
    return null;
  }
}

api.interceptors.request.use((config) => {
  // Read token from the canonical key written by AuthContext.login().
  // Fall back to vecindus_auth (session blob) for backward compatibility.
  let token = localStorage.getItem('token');
  let user = null;
  try {
    user = JSON.parse(localStorage.getItem('user') || 'null');
  } catch {
    user = null;
  }
  if (!token) {
    try {
      const stored = JSON.parse(localStorage.getItem('vecindus_auth') || '{}');
      token = stored.token || '';
      if (!user && stored.user) user = stored.user;
    } catch {
      // ignore parse errors
    }
  }
  if (token) config.headers.Authorization = `Bearer ${token}`;
  // Entitlement Sprint 2 (spec E3): send the active owned-unit context on every
  // authenticated request. Maintained by AuthContext.setActiveOwnedUnit; the
  // server validates it (unit exists + caller has access) before using it.
  if (token) {
    // TH1 stale-id lane (2026-10-01): an explicit unitId param is the primary
    // signal — the header MUST match it. Previously the 404-recovery retry
    // that swapped in a corrected unitId still carried the stale/absent
    // X-Active-Unit-Id and failed the membership/unit check with 403.
    const activeUnitId =
      (config.params && typeof config.params.unitId === 'string' && config.params.unitId) ||
      localStorage.getItem('vecindus_active_owned_unit');
    // Belt-and-braces: a /home/* call referencing a unit that is NOT in the
    // known-units snapshot short-circuits to the caller's error state (visible
    // ES error with Reintentar) instead of a guaranteed 404/403 round-trip.
    // Skipped while the snapshot is missing (ownedUnits still loading) so
    // legit flows are never broken.
    const isHomeUrl = typeof config.url === 'string' && config.url.includes('/home/');
    const knownUnits = isHomeUrl && activeUnitId ? readKnownUnitIds() : null;
    if (knownUnits && !knownUnits.has(String(activeUnitId))) {
      traceSel('staleUnitBlocked', { url: config.url || '', unitId: activeUnitId, headerId: localStorage.getItem('vecindus_active_owned_unit') });
      // Beacon: leading indicator of the TH1 stale-id class (session-deduped).
      beaconStateAnomaly({ event: 'staleUnitBlocked', deadUnitId: String(activeUnitId), condoId: localStorage.getItem('vecindus_active_condo'), fallbackUnitId: null, repaired: false });
      const staleUnitError = new Error(`Unknown active unit (stale selection): ${activeUnitId}`);
      (staleUnitError as Error & { code?: string }).code = 'VECINDUS_STALE_ACTIVE_UNIT';
      return Promise.reject(staleUnitError);
    }
    if (activeUnitId) config.headers['X-Active-Unit-Id'] = activeUnitId;
  }
  // NOTE: X-User-Id, X-User-Role, X-Condo-Id headers intentionally REMOVED (P0/C5 fix).
  // The backend never trusts these headers — they were a defense-in-depth footgun.
  return config;
});

api.interceptors.response.use(
  (response) => {
    // Phase-1 429 fix (2026-09-30): any successful response clears the 429
    // backoff gate — the bucket has recovered, background polling resumes.
    reset429Backoff();
    // TH1 diagnostic lane (2026-09-30): every /home/* request lands in the
    // selection-trace ring buffer (url, unitId param, X-Active-Unit-Id,
    // status) so a silent blank can be correlated with the exact unit the
    // client thought was active. ~6s polls are fine: the ring holds the last
    // ~50 entries (~5 minutes of polling history).
    {
      const cfgUrl: string = response.config?.url || '';
      if (cfgUrl.includes('/home/')) {
        traceSel('api', { url: cfgUrl, unitId: response.config?.params?.unitId ?? null, xActiveUnitId: response.config?.headers?.['X-Active-Unit-Id'] ?? null, status: response.status });
      }
    }
    // Guard: if the server (or a proxy like nginx) returns an HTML error page
    // with a 200 status, detect it and reject so callers' catch blocks fire.
    const ct = String(response.headers?.['content-type'] || '');
    const data = response.data;
    if (typeof data === 'string' && (ct.includes('text/html') || data.trimStart().startsWith('<'))) {
      return Promise.reject(new Error('Server returned HTML instead of JSON (likely a proxy error page)'));
    }
    return response;
  },
  async (error) => {
    // Phase-1 429 fix (2026-09-30): arm the exponential backoff gate on every
    // 429 (Retry-After honored, doubling per consecutive 429, cap 5 min).
    // No retry is attempted here — callers/polls observe the gate instead.
    if (error.response?.status === 429) {
      note429(error.response?.headers?.['retry-after']);
    }
    // TH1 diagnostic lane (2026-09-30): /home/* failures (400 'unitId is
    // required', 401s, offline 503s) are traced with the same fields so the
    // ring buffer shows exactly what the client sent when it failed.
    {
      const errUrl: string = (error.config || ({} as any))?.url || '';
      if (errUrl.includes('/home/')) {
        traceSel('apiError', { url: errUrl, unitId: error.config?.params?.unitId ?? null, xActiveUnitId: error.config?.headers?.['X-Active-Unit-Id'] ?? null, status: error.response?.status ?? null, message: error.message });
      }
    }
    const status401 = error.response?.status === 401;
    const config = error.config || ({} as any);
    const requestUrl: string = config.url || '';
    // /auth/2fa/verify 401s (bad/expired code) must surface inline, not wipe the session.
    const isAuthEndpoint = requestUrl.includes('/auth/') && !requestUrl.includes('/auth/2fa/verify');
    const isRefreshCall = requestUrl.includes('/auth/refresh');
    // iOS session fix (2026-09-18): pre-login / registration endpoints must
    // NEVER trigger a refresh attempt; their 401s go straight to the wipe path.
    const isPreLoginAuth = /\/auth\/(login|register)/.test(requestUrl);
    // Silent refresh only applies when a session actually existed (never during
    // pre-login flows — wrong-password 401s must not trigger token refresh).
    const hadSession = !!localStorage.getItem('token');

    if (status401 && hadSession && !isRefreshCall) {
      if (isAuthEndpoint && (isPreLoginAuth || config._vecindusSilentRetried)) {
        // ONLY log out when the 401 comes from a pre-login auth endpoint
        // (e.g. /api/auth/login, /api/auth/register) or an auth-endpoint retry
        // that already got its one refresh chance. Data-endpoint 401s and
        // expired-session auth GETs (e.g. /auth/me on cold open) go through the
        // silent-refresh path below. This prevents a bug where deleting a task
        // (or other data operations) that return 401 for any reason would
        // unexpectedly log the user out.
        localStorage.removeItem('token');
        localStorage.removeItem('vecindus_auth');
        localStorage.removeItem('user');
        window.location.href = '/login';
      } else if (!config._vecindusSilentRetried) {
        // iOS session fix (2026-09-18): /auth/me (and other auth GETs issued
        // with a stale access token on app resume) previously wiped the session
        // immediately, causing iOS logouts on cold open. Attempt ONE silent
        // refresh + retry before giving up.
        // Silent session refresh (2026-09-17, Jaime-approved): on a data-endpoint 401
        // attempt ONE refresh + retry the original request. Intentional logouts and
        // 403s never reach this path (logout clears the refresh token; 403 is not 401).
        // Silent session refresh (2026-09-17, Jaime-approved): on a data-endpoint 401
        // attempt ONE refresh + retry the original request. Intentional logouts and
        // 403s never reach this path (logout clears the refresh token; 403 is not 401).
        const refreshed = await refreshSessionTokens();
        if (refreshed) {
          config._vecindusSilentRetried = true;
          config.headers = { ...(config.headers || {}) };
          config.headers.Authorization = `Bearer ${refreshed.accessToken}`;
          // If the retry also 401s it re-enters this handler with the flag set,
          // which falls through to the session-wipe below — no infinite loops.
          return api.request(config);
        }
        clearLocalSession();
      } else {
        // The retried request 401'd again even after a fresh token → session dead.
        clearLocalSession();
      }
    }

    // Plan-limit popup feature (2026-08-31, Jaime): ANY 403 with a plan-limit
    // rejection (code 'PLAN_LIMIT', message fallback) opens the global upgrade
    // modal app-wide — personal, residentes, mascotas, vehículos, calendario,
    // etc. — without per-page wiring. Dedupe is handled in the modal store.
    if (isPlanLimitError(error)) {
      showPlanLimitModal(planLimitInfoFromError(error));
    }

    // CLIENT_ERROR: register the failure server-side (fire-and-forget). Expected
    // flows (401/403 handled auth/plan-limit, 413 FILE_TOO_LARGE) are skipped
    // inside the reporter.
    // Surface ownership (visible-error beacon, 2026-10-01): /home/* failures
    // are reported by the LoadError banner (components/HomeStateBanners.tsx)
    // as kind 'visible_error' — the interceptor must NOT double-report them.
    {
      const failedUrl = String(error?.config?.url || '');
      if (!failedUrl.includes('/home/')) {
        try {
          reportApiFailure(error, { route: window.location.pathname });
        } catch {
          /* never break the interceptor because of the reporter */
        }
      }
    }

    return Promise.reject(error);
  }
);

/**
 * Session invalidation for silent-refresh failures. Only called when a real
 * session existed and the refresh flow cannot restore it. Does NOT touch
 * plan-limit/403 handling or the PushEnableCard flow.
 */
function clearLocalSession() {
  localStorage.removeItem('token');
  localStorage.removeItem('vecindus_auth');
  localStorage.removeItem('user');
  localStorage.removeItem('refreshToken');
  window.location.href = '/login';
}

let refreshInFlight: Promise<{ accessToken: string; refreshToken: string } | null> | null = null;

/**
 * Silent refresh (2026-09-17, Jaime-approved iOS/session fix).
 * POST /api/auth/refresh with the stored refresh token (kept in localStorage
 * by design in this pass). De-duplicates concurrent refreshes via a shared
 * in-flight promise. On success persists the new access + refresh tokens and
 * returns them; on failure returns null WITHOUT clearing storage (the caller
 * decides — the interceptor clears the session, launch restore just falls
 * back to the login screen).
 */
export function refreshSessionTokens(): Promise<{ accessToken: string; refreshToken: string } | null> {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) return Promise.resolve(null);
  if (!refreshInFlight) {
    refreshInFlight = fetch(`${API_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    })
      .then(async (res) => {
        if (!res.ok) return null;
        const payload = await res.json().catch(() => null);
        const data = payload?.data || payload || {};
        const accessToken = data.accessToken || data.tokens?.accessToken || '';
        const nextRefresh = data.refreshToken || data.tokens?.refreshToken || '';
        if (!accessToken) return null;
        localStorage.setItem('token', accessToken);
        if (nextRefresh) localStorage.setItem('refreshToken', nextRefresh);
        // Keep the vecindus_auth session blob in sync so a mid-session reload
        // restores with the CURRENT access token instead of the stale one.
        try {
          const blob = JSON.parse(localStorage.getItem('vecindus_auth') || 'null');
          if (blob) {
            blob.token = accessToken;
            blob.timestamp = Date.now();
            localStorage.setItem('vecindus_auth', JSON.stringify(blob));
          }
        } catch { /* blob optional */ }
        return { accessToken, refreshToken: nextRefresh || refreshToken };
      })
      .catch(() => null)
      .finally(() => { refreshInFlight = null; });
  }
  return refreshInFlight;
}

export const authAPI = {
  // Identity batch 2 (item F, 2026-09-16): consolidated onto the
  // phone-capable /auth/login-password endpoint (batch 1). NOTE: the
  // response shape DIFFERS from the legacy /auth/login — login-password
  // returns the user at data.user (no top-level `user` field). No in-repo
  // callers existed for authAPI.login; if any appear, they must read
  // response.data.data.user, not response.data.user.
  login: (email: string, password: string) =>
    api.post('/auth/login-password', { email, password }),
  enroll2FA: () => api.post('/auth/2fa/enroll'),
  enable2FA: (code: string) => api.post('/auth/2fa/enable', { code }),
  disable2FA: (code: string) => api.post('/auth/2fa/disable', { code }),
  verify2FA: (pendingToken: string, code: string) => api.post('/auth/2fa/verify', { pendingToken, code }),
  forgotPassword: (email: string) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token: string, password: string) =>
    api.post('/auth/reset-password', { token, password }),
  register: (data: any) => api.post('/auth/register', data),
  verifyEmail: (token: string) => api.get('/auth/verify-email', { params: { token } }),
  resendVerification: () => api.post('/auth/resend-verification'),
  deleteAccount: (password: string) =>
    api.delete('/auth/account', { data: { password } }),
  // Token revocation (2026-09-29): logout now invalidates the token pair
  // server-side. We send the stored refreshToken so BOTH tokens get revoked;
  // the access token is revoked server-side from the Authorization header.
  // localStorage is cleared on BOTH paths (success and failure) — a failed
  // server call must never leave the client logged in.
  logout: async (refreshToken?: string | null, accessToken?: string | null): Promise<void> => {
    const stored = refreshToken ?? localStorage.getItem('refreshToken');
    const access = accessToken ?? localStorage.getItem('token');
    try {
      // 2026-10-01 logout-401 fix: AuthContext captures BOTH tokens before
      // clearAuth() wipes localStorage, then passes them here explicitly.
      // Without the explicit Authorization header the request interceptor
      // finds no token in the (already-cleared) storage and the server
      // rejects the revocation with 401. When no access token exists the
      // server cannot authenticate the call anyway, so skip the POST
      // gracefully — the local session is still cleared in `finally`.
      if (access) {
        await api.post('/auth/logout', stored ? { refreshToken: stored } : {}, {
          headers: { Authorization: `Bearer ${access}` },
        });
      }
    } catch (err) {
      // Swallow network/5xx: local session must be cleared regardless.
      // Never log the raw axios error object: err.config carries the
      // Authorization header and the refreshToken body (token material).
      console.warn('[auth] logout request failed, clearing local session anyway:',
        err instanceof Error ? err.message : 'unknown error',
        (err as any)?.response?.status ?? 'no status');
    } finally {
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('vecindus_auth');
    }
  },
  getMe: () => api.get('/auth/me'),
  getOwnedUnits: () => api.get('/units/mine'),
  getContexts: () => api.get('/auth/contexts'),
  activateContext: (contextId: string) => api.post(`/auth/contexts/${contextId}/activate`, {}),
  getPermissions: (condoId?: string) => api.get('/me/permissions', { params: condoId ? { condoId } : undefined }),
  setActiveCondo: (condoId: string | null) => api.post('/me/active-condo', { condoId }),
};

export const employeesAPI = {
  // Ruling 2026-09-09 (Jaime): backend defaults to scope=condo (condo-level
  // employees only). scope='all' returns the full directory including
  // unit-linked staff — StaffDirectory's condo-wide lens needs that slice.
  getAll: (condoId: string, scope: 'condo' | 'all' = 'condo') =>
    api.get(`/condos/${condoId}/employees`, { params: { scope } }),
  // create data (ASSUMED CONTRACT 2026-08-31, pending alignment pass):
  // - New user: { name, email, phone?, employeeRole, ... } => response may include
  //   { inviteUrl } (invite-link flow) or employee.tempPassword (legacy).
  // - Existing user: { mode:'existing', email|phone, employeeRole, ... }; 404 => unknown user.
  create: (condoId: string, data: any) => api.post(`/condos/${condoId}/employees`, data),
  update: (condoId: string, employeeId: string, data: any) => api.patch(`/condos/${condoId}/employees/${employeeId}`, data),
  revoke: (condoId: string, employeeId: string, reason: string) => api.post(`/condos/${condoId}/employees/${employeeId}/revoke`, { reason }),
  // Spec 2026-08-31 (Jaime): split "Eliminar/Revocar acceso" into two actions.
  // deactivate: SOFTER action ("Desactivar acceso"). The backend currently has
  // NO dedicated deactivate route; the closest real contract is
  // POST /condos/:condoId/employees/:employeeId/revoke { reason }
  // (src/modules/employees/routes.js), which sets status='revoked' and
  // deactivates the membership while the row stays listed. The list payload
  // exposes `status`, and the UI renders revoked as "Inactivo". GAP: a true
  // soft-deactivate (status='inactive') endpoint is still in flight on the
  // backend; swap this call when it lands.
  deactivate: (condoId: string, employeeId: string, reason: string) => api.post(`/condos/${condoId}/employees/${employeeId}/revoke`, { reason }),
  // GAP (2026-08-31): ASSUMED CONTRACT — POST .../reactivate does NOT exist in
  // src/modules/employees/routes.js yet. UI ships the "Reactivar" action; it
  // will surface a friendly error until the backend lands this route.
  reactivate: (condoId: string, employeeId: string) => api.post(`/condos/${condoId}/employees/${employeeId}/reactivate`, {}),
  // GAP (2026-08-31): ASSUMED CONTRACT — DELETE /condos/:condoId/employees/:employeeId
  // does NOT exist in src/modules/employees/routes.js yet (only the units
  // variant DELETE .../units/:unitId). UI ships "Eliminar del Personal" against
  // this assumed route; backend must implement remove-from-list (keep the
  // Vecindus user account) or the UI will surface a friendly error.
  remove: (condoId: string, employeeId: string) => api.delete(`/condos/${condoId}/employees/${employeeId}`),
  // Employee access-link (Jaime req 2026-08-31): POST /api/employees/:userId/access-link
  // → { inviteUrl }. Single-use link, expires in 7 days. If the employee has no
  // user account yet the API errors — the UI surfaces the API message.
  accessLink: (userId: string) => api.post(`/employees/${encodeURIComponent(userId)}/access-link`, {}),
  // Staff self-permission probe: returns { isStaff, canSeeColleagues, canSeeWages }.
  permissions: (condoId: string) => api.get(`/condos/${condoId}/me/staff-permissions`),
};

export const unitsAPI = {
  // Units are mounted by the backend at /api/units/condo/:condoId.
  getAll: (condoId: string) => api.get(`/units/condo/${condoId}`),
  mine: () => api.get('/units/mine'),
  getForCondo: (condoId: string) => api.get(`/condos/${condoId}/units`),
  getStandalone: (q = '') => api.get('/units/standalone', { params: q.trim() ? { q: q.trim() } : undefined }),
  remove: (unitId: string) => api.delete(`/units/${unitId}`),
  getAvailable: (condoId: string) => api.get(`/condos/${condoId}/units/available`),
  // Standalone unit creation (no condo): requires full address_data per backend validation.
  create: (data: any) => api.post('/units', data),
  createInCondo: (condoId: string, data: any) => api.post(`/units/condo/${condoId}`, data),
  update: (unitId: string, data: any) => api.patch(`/units/${unitId}`, data),
  // Standalone units (no condo): POST /api/units/:unitId/residents — the unit
  // owner (units.owner_user_id or active 'propietario' membership) adds a
  // resident to THEIR unit without any condo context. Body contract
  // (src/modules/unit/routes.js): { name, email, generateInvite?, role?,
  // mode:'existing', email|phone } — response data.invite.url on invite flows.
  addResidentToUnit: (unitId: string, data: any) =>
    api.post(`/units/${unitId}/residents`, data),
  // FIX 2026-08-31 (Usuarios, Bug A): standalone units have no condo, so the
  // condo people endpoint (/condos/:condoId/people) cannot list them. There is
  // NO dedicated GET /units/:id/residents list endpoint (flagged to backend);
  // GET /units/:id returns { unit: { ..., residents: ucr[] } } with active
  // user_condo_roles rows joined to users (src/modules/unit/routes.js ->
  // Units.getWithResidents). Access: unit owner, condo role, or super_admin.
  getResidents: (unitId: string) => api.get(`/units/${unitId}`),
  // 2026-09-07: unit-keyed person edit — PATCH /api/units/:unitId/residents/:userId.
  // Used when the active context is a standalone unit (no condoId): the unit
  // owner edits a resident of THEIR unit without any condo context. Same
  // editable-field payload shape as condosAPI.updatePerson (name/phone/bio/
  // role/unit_id/active/subType). Backend: src/modules/unit/routes.js.
  updateUnitResident: (unitId: string, userId: string, data: { name?: string; phone?: string; bio?: string; role?: string; subType?: string | null; unit_id?: string | null; active?: boolean }) =>
    api.patch(`/units/${unitId}/residents/${userId}`, data),
};

export const entitlementsAPI = {
  // Effective entitlement for a specific unit context (Entitlement Sprint 2, E2/E3).
  // Returns { condoPaid, unitPaid, homeUltra, plan, features, source, condoId, unitId }.
  getForUnit: (unitId: string) => api.get('/entitlements/me', { params: { unitId } }),
};

export const sectionsAPI = {
  getAll: (condoId: string) => api.get(`/condos/${condoId}/sections`),
  create: (condoId: string, data: { name: string; type: string; plan?: string; parentSectionId?: string | null }) =>
    api.post(`/condos/${condoId}/sections`, data),
  update: (condoId: string, sectionId: string, data: { name?: string; type?: string; plan?: string; parentSectionId?: string | null }) =>
    api.patch(`/condos/${condoId}/sections/${sectionId}`, data),
  remove: (condoId: string, sectionId: string, force = false) =>
    api.delete(`/condos/${condoId}/sections/${sectionId}`, { data: force ? { force: true } : undefined }),
};

export const condosAPI = {
  people: (condoId: string) => api.get(`/condos/${condoId}/people`),
  // Resident creation — POST /api/condos/:condoId/members (condo router POST /:id/members).
  // TWO MODES (Jaime req 2026-08-31; ASSUMED CONTRACTS — pending backend alignment pass):
  // - New user (invite flow): { name, email, password?, role, unitId }. Password is
  //   OPTIONAL: when omitted the backend creates the user without a password and
  //   should return { inviteUrl } (single-use invite URL). If inviteUrl is absent
  //   the UI falls back to legacy temp-password behavior.
  // - Existing user attach: { mode:'existing', email|phone, role, unitId }.
  //   404 => no user with that email/phone (UI shows friendly error).
  // role must be backend-valid ('resident' | 'propietario' | 'familiar' | 'empleado' | ...).
  // Propietarios may only add resident/familiar/empleado to units they own.
  createResident: (condoId: string, data: { mode?: 'existing'; name?: string; email?: string; phone?: string; password?: string; generateInvite?: boolean; unitId?: string; role?: string }) =>
    api.post(`/condos/${condoId}/members`, data),
  // Person edit — FINAL contract (2026-08-31): PATCH /api/condos/:condoId/members/:userId
  // keyed by USER id (people endpoint returns userId per row). Editable:
  // name/phone/bio/role/unit_id/active/subType. Email is NOT editable here
  // (super-admin only, PUT /api/users/:id).
  updatePerson: (condoId: string, userId: string, data: { name?: string; phone?: string; bio?: string; role?: string; subType?: string | null; unit_id?: string | null; active?: boolean }) =>
    api.patch(`/condos/${condoId}/members/${userId}`, data),
  // Resident "Desactivar acceso" — FINAL contract (2026-08-31, contract-alignment):
  // PATCH /api/condos/:condoId/memberships/:membershipId/deactivate { reason? }
  // sets the membership row inactive while the person stays listed (status
  // 'inactive'). Keyed by MEMBERSHIP id from the people endpoint row.
  deactivatePerson: (condoId: string, membershipId: string, reason?: string) =>
    api.patch(`/condos/${condoId}/memberships/${membershipId}/deactivate`, { reason }),
  // Resident "Reactivar" — FINAL contract (2026-08-31, contract-alignment):
  // PATCH /api/condos/:condoId/memberships/:membershipId/reactivate restores
  // the membership to status 'active'.
  reactivatePerson: (condoId: string, membershipId: string) =>
    api.patch(`/condos/${condoId}/memberships/${membershipId}/reactivate`, {}),
  // GAP (2026-08-31): ASSUMED CONTRACT — DELETE /api/units/:unitId/residents/:userId
  // ("remove from unit ONLY; the Vecindus account is kept") does NOT exist in
  // src/modules/unit/routes.js yet. The condo-level DELETE
  // /condos/:condoId/members/:userId soft-deletes + ANONYMIZES the whole user
  // account, which contradicts the required "Eliminar de la unidad" copy, so it
  // is deliberately NOT used here. Pending backend alignment pass.
  removeResidentFromUnit: (unitId: string, userId: string) =>
    api.delete(`/units/${unitId}/residents/${userId}`),
  // Ownership transfer — FINAL contract (2026-08-31): POST /api/units/:unitId/transfer-ownership
  // (units router, NOT /condos). Body { email }. Response:
  // { success, data: { unitId, previousOwnerId, ownerId, auditRequestId } }
  // or { success, data: { unitId, ownerId, alreadyOwner: true } }.
  transferOwnership: (unitId: string, targetEmail: string) =>
    api.post(`/units/${unitId}/transfer-ownership`, { email: targetEmail }),
  getAll: (condoId?: string) => api.get('/condos', { params: condoId ? { condoId } : undefined }),
  create: (data: any) => api.post('/condos', data),
  getById: (id: string) => api.get(`/condos/${id}`),
  update: (id: string, data: any) => api.put(`/condos/${id}`, data),
  // NEW (P3): List all available condos for join/registration flow
  getAvailable: (search?: string) =>
    api.get('/condos/available', { params: search ? { search } : {} }),
  // NEW (P3): Join a condominium
  join: (data: { condoId: string; unitLabel?: string; role?: string }) =>
    api.post('/condos/join', data),
  addMember: (condoId: string, data: any) => api.post(`/condos/${condoId}/members`, data),
  // P2-A (2026-09-08, spec ADDENDUM): neighbors directory — membership-gated;
  // returns ONLY directory_visible members: {id, name, avatarUrl, unitLabel, role, bio}.
  neighbors: (condoId: string) => api.get(`/condos/${condoId}/neighbors`),
  // F18 roster for residents (2026-09-24): non-privileged viewers get the
  // limited-field branch ONLY: { user_id, name, role, unit_id, active } — no
  // email/phone. Privacy filters (opt-out, blocks, minors) are server-side.
  getRoster: (condoId: string) => api.get(`/condos/${condoId}/members`),
  // F18 self-serve per-condo directory opt-out (2026-09-23): updates ALL active
  // memberships of the caller. visible=false = bidirectional invisibility.
  setMyDirectoryVisibility: (condoId: string, visible: boolean) =>
    api.put(`/condos/${condoId}/my-directory-visibility`, { visible }),
  // Narrow birthdays feed (flag-independent): GET /api/condos/:id/neighbors/birthdays
  neighborsBirthdays: (condoId: string) => api.get(`/condos/${condoId}/neighbors/birthdays`),
};

export const usersAPI = {
  getAll: (condoId?: string) => api.get('/users', { params: condoId ? { condoId } : undefined }),
  create: (data: any) => api.post('/users', data),
  delete: (id: string) => api.delete(`/users/${id}`),
  // BUG #150 (2026-09-24): role/condoId — changing an existing membership's
  // role (e.g. empleado→residente) through the same user-update path.
  // Backend: PUT /api/users/:id — profile fields stay super_admin-only;
  // role change allowed for super_admin + the membership's condo admins.
  update: (id: string, data: { name: string; email: string; phone?: string | null; role?: string; condoId?: string }) => api.put(`/users/${id}`, data),
  // PATCH /api/users/:id/beta — super_admin-only beta-user toggle.
  // Backend contract: 200 → { success: true, data: { id, is_beta } }
  // (frontend also tolerates { ok: true, user } shape).
  setBeta: (id: string, is_beta: boolean) => api.patch(`/users/${id}/beta`, { is_beta }),
  upgradeToSupplier: () => api.post('/users/upgrade-to-supplier'),
  // Reenviar enlace de invitación (Jaime add-on 2026-08-31).
  // Contract (backend invites/routes.js): POST /api/invites/resend { userId }
  // → { success, data: { userId, email, invite: { url, expiresAt, token } } }.
  // Only for passwordless (invite-pending) users; 409 ALREADY_SET otherwise.
  reinvite: (userId: string) => api.post('/invites/resend', { userId }),
  updateProfile: (data: any) => api.put('/users/profile', data),
  getById: (id: string) => api.get(`/users/${id}`),
  // F18 self-serve block (backend 2026-09-24, migration 100 user_condo_blocks):
  // POST /api/users/:id/block — bidirectional visibility kill on rosters +
  // refuses DMs between the pair (either direction; DM attempts get 403 BLOCKED).
  blockUser: (userId: string) => api.post(`/users/${userId}/block`),
  // DELETE /api/users/:id/block — unblock.
  unblockUser: (userId: string) => api.delete(`/users/${userId}/block`),
  // GET /api/users/me/blocks — { blocks: [{ user_id, name, avatar_url, created_at }], count }.
  myBlocks: () => api.get('/users/me/blocks'),
};

// Invite-link flow (2026-08-31, Jaime): public token endpoints (no auth required).
// GET  /api/invites/:token        → { success, data: inviteInfo }
// POST /api/invites/:token/accept → { password } sets first password (single use).
// GET  /api/invites/resend is authed and handled via usersAPI.reinvite.
export const invitesAPI = {
  getInfo: (token: string) => api.get(`/invites/${encodeURIComponent(token)}`),
  accept: (token: string, password: string) =>
    api.post(`/invites/${encodeURIComponent(token)}/accept`, { password }),
};

export const moduleConfigAPI = {
  getAll: (condoId: string) => api.get(`/condos/${condoId}/modules`),
  update: (condoId: string, modules: any) => api.post(`/condos/${condoId}/modules`, { modules }),
};

export const residenceAPI = {
  getResidents: (residenceId: string) => api.get(`/residences/${residenceId}/residents`),
  createResident: (residenceId: string, data: any) => api.post(`/residences/${residenceId}/residents`, data),
  suspendResident: (residenceId: string, userId: string) => api.patch(`/residences/${residenceId}/residents/${userId}/suspend`),
  deleteResident: (residenceId: string, userId: string) => api.delete(`/residences/${residenceId}/residents/${userId}`),
};

export const announcementsAPI = {
  getAll: (condoId: string) => api.get(`/announcements/condo/${condoId}`),
  create: (condoId: string, data: any) => api.post(`/announcements/condo/${condoId}`, data),
  delete: (id: string) => api.delete(`/announcements/${id}`),
  update: (id: string, data: any) => api.put(`/announcements/${id}`, data),
};

export const calendarAPI = {
  getEvents: (condoId: string) => api.get(`/calendar/condo/${condoId}/events`),
  createEvent: (condoId: string, data: any) => api.post(`/calendar/condo/${condoId}/events`, data),
  updateEvent: (id: string, data: any) => api.put(`/calendar/events/${id}`, data),
  deleteEvent: (id: string) => api.delete(`/calendar/events/${id}`),
  getRsvps: (condoId: string, eventId: string) =>
    api.get(`/calendar/condo/${condoId}/events/${eventId}/rsvps`),
  rsvp: (condoId: string, eventId: string, status: string) =>
    api.post(`/calendar/condo/${condoId}/events/${eventId}/rsvp`, { status }),
  editEvent: (condoId: string, eventId: string, data: any) =>
    api.put(`/calendar/events/${eventId}`, {
      ...data,
      eventDate: data.eventDate || data.date,
    }),
};

export const expensesAPI = {
  getAll: (condoId: string) => api.get(`/expenses/condo/${condoId}`),
  create: (condoId: string, data: any) => api.post(`/expenses/condo/${condoId}`, data),
  update: (id: string, data: any) => api.put(`/expenses/${id}`, data),
  delete: (id: string) => api.delete(`/expenses/${id}`),
  // BUG #147: backend POST /expenses/:id/receipt accepts JSON only —
  // { file: <raw base64>, fileName, mimeType }. Multipart is never parsed
  // (no multer) and always 400s ("File data is required"). Send the contract
  // directly; 30s timeout so a server hang surfaces as an error toast instead
  // of a silent infinite spinner.
  uploadReceipt: async (expenseId: string, file: File) => {
    const base64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result.includes(',') ? result.split(',')[1] : result);
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
    return api.post(
      `/expenses/${expenseId}/receipt`,
      { file: base64, fileName: file.name, mimeType: file.type || undefined },
      { timeout: 30000 }
    );
  },
  uploadReceiptBase64: (expenseId: string, base64: string, mimeType?: string, fileName?: string) =>
    api.post(
      `/expenses/${expenseId}/receipt`,
      { file: base64, mimeType, fileName },
      { timeout: 30000 }
    ),
};

export const financialAPI = {
  getReports: (condoId: string) => api.get(`/financial/condo/${condoId}/reports`),
  getSummary: (condoId: string) => api.get(`/financial/condo/${condoId}/summary`),
  getMorosos: (condoId: string) => api.get(`/financial/condo/${condoId}/morosos`),
  getClosures: (condoId: string) => api.get(`/financial/condo/${condoId}/closures`),
  // Backend currently exposes GET /financial/condo/:condoId/income only;
  // keep the compatibility symbol without inventing a write route.
  addIncome: (condoId: string, data: any) =>
    api.post(`/financial/charges/condo/${condoId}`, data),
  registerPayment: (condoId: string, data: any) =>
    api.post('/financial/payments', { ...data, condo_id: condoId }),
};

export const amenitiesAPI = {
  getAll: (condoId: string) => api.get(`/amenities/condo/${condoId}`),
  getBookings: (condoId: string) => api.get(`/amenities/bookings/condo/${condoId}`),
  book: (data: { amenityName: string; bookingDate: string; startTime: string; endTime: string; condoId: string; unit?: string }) =>
    api.post('/amenities/book', data),
  getAvailability: (amenityId: string, date: string) => api.get(`/amenities/${amenityId}/availability?date=${date}`),
  createReservation: (data: any) => api.post('/reservations', data),
  getMyReservations: (userId: string) => api.get(`/reservations/user/${userId}`),
  create: (condoId: string, data: any) => api.post(`/amenities/condo/${condoId}`, data),
  update: (condoId: string, amenityId: string, data: any) =>
    api.put(`/amenities/condo/${condoId}/${amenityId}`, data),
  updatePricing: (condoId: string, amenityId: string, data: any) =>
    api.put(`/amenities/condo/${condoId}/${amenityId}/pricing`, data),
};

export const votingAPI = {
  getAll: (condoId: string) => api.get(`/voting/condo/${condoId}`),
  create: (condoId: string, data: any) => api.post(`/voting/condo/${condoId}`, data),
  vote: (votingId: string, optionId: string) => api.post(`/voting/${votingId}/vote`, { optionId }),
  getResults: (votingId: string) => api.get(`/voting/${votingId}/results`),
  close: (votingId: string) => api.put(`/voting/${votingId}/close`),
  delete: (votingId: string) => api.delete(`/voting/${votingId}`),
};

export const securityAPI = {
  getCasetas: (condoId: string) => api.get(`/security/condo/${condoId}/casetas`),
  getAlerts: (condoId: string) => api.get(`/security/condo/${condoId}/alerts`),
  sendPanicAlert: (data: any) => api.post('/security/panic-alert', data),
  resolveAlert: (alertId: string) => api.patch(`/security/alerts/${alertId}/resolve`),
  getVisitors: (condoId: string) => api.get(`/security/condo/${condoId}/visitors`),
  createVisitorCode: (data: any) => api.post('/visitor-codes', data),
  // BUG FIX (2026-08-21): POST /visitor-codes/verify does not exist (404).
  // The working validation endpoint is GET /qr-access/validate/:code.
  // FIX 2026-09-09 (direction support): GET /qr-access/validate/:code now accepts
  // ?direction=entrada|salida (BackendDev contract). Sent only when the scanner
  // picked a direction; older backends ignore the extra param (graceful).
  // FIX 2026-09-14 (Jaime-approved Option B): refreshOnly=true revalidates the
  // code and returns the same card WITHOUT recording a scan event or running
  // direction/ALREADY_INSIDE logic — used by the photo-ID refresh callbacks.
  // FIX 2026-09-15 (ADDENDUM 2026-09-14b Phase 2 FE): explicit ?condoId= of the
  // condo the scanner UI is operating in. VQR2 user-identity payloads resolve
  // against the SCANNER'S condo; sending it explicitly supersedes the backend
  // scannerPrimaryCondoId fallback. Older backends ignore the extra param.
  verifyCode: (code: string, direction?: 'entrada' | 'salida', refreshOnly?: boolean, condoId?: string) =>
    api.get(`/qr-access/validate/${encodeURIComponent(code)}`, {
      params: {
        ...(direction ? { direction } : {}),
        ...(refreshOnly ? { refreshOnly: '1' } : {}),
        ...(condoId ? { condoId } : {}),
      },
    }),
  // GET /qr-access/audit?condoId= — condo-wide scan audit (BackendDev, 2026-09-09).
  // Response shape coded defensively: { data: { entries | audit | scans: [...] } }
  // or a bare array. See QrAuditPage.tsx normalizeAuditEntry().
  audit: (condoId: string) => api.get('/qr-access/audit', { params: { condoId } }),
};

// ============================================================
// Phase 2 — Persistent Visitor Registry (plan tasks 9–11, 2026-09-08)
// Spec: VECINDUS_FUNCTION_SPEC.md ADDENDUM 2026-09-08 (c) §6 + FINAL RULINGS.
// Privacy rails (server-enforced): GET /visitors and POST /reinvite only ever
// return/accept visitors with presence in the caller's OWN condo (403
// NO_PRESENCE_IN_CONDO otherwise); employees are 403'd server-side.
// POST /access/id-capture is scanner-roles only (admin/sub_admin/seguridad/
// super_admin) — residents/employees get 403 from the backend.
// ============================================================
export const visitorRegistryAPI = {
  // GET /qr-access/visitors?condoId= — presence-scoped known visitors for the
  // re-invite picker. Items: { visitor_profile_id, full_name, phone,
  // photo_url, linked, invite_count, last_invited_at, id_on_file }.
  listKnown: (condoId: string) =>
    api.get('/qr-access/visitors', { params: { condoId } }),
  // POST /qr-access/reinvite — one-tap re-invite by profile. Body:
  // { condoId, visitorProfileId, arrivalAt (ISO), windowHours }.
  // Backend delegates to the canonical generator and returns the same
  // envelope as /generate ({ data: { code: <access_codes row> } }).
  reinvite: (data: {
    condoId: string;
    visitorProfileId: string;
    arrivalAt: string;
    windowHours: number;
    vehiclePlate?: string;
    // 2026-09-15: optional visitor email — saved to visitor_profiles.email on
    // the backend so the invite-link email sender can use it on re-invite.
    visitorEmail?: string;
  }) => api.post('/qr-access/reinvite', data),
  // POST /access/id-capture — gate-side ID document scan (Ruling 3(b)).
  // Body: { condoId, image (base64 or data-URL, <=5MB, magic-byte checked),
  //         code? | name+phone? }. Resolve by scanned access code when known.
  captureId: (data: {
    condoId: string;
    image: string;
    code?: string;
    name?: string;
    phone?: string;
  }) => api.post('/access/id-capture', data),
};

export const complaintsAPI = {
  getAll: (condoId: string) => api.get(`/complaints/condo/${condoId}`),
  getStats: (condoId: string) => api.get(`/complaints/condo/${condoId}/stats`),
  // A1: app-scope (Soporte Vecindus) entries — super_admin only endpoint.
  getApp: () => api.get('/complaints/app'),
  // 2026-09-02 (Jaime): the caller's OWN app-scope threads, keyed by userId
  // alone — unit/condo-independent, any role.
  getMineApp: () => api.get('/complaints/mine/app'),
  getMineAppStats: () => api.get('/complaints/mine/app/stats'),
  // PACKET (2026-09-24, Jaime): super-admin bugs trend chart — found/day +
  // resolved/day time series. Backend GET /api/complaints/bugs/trends (window
  // or from/to). resolved/day is only reliable from migration 146 day forward
  // (resolvedTrackingFrom in the payload).
  getBugTrends: (params: { window?: number | string; from?: string; to?: string }) =>
    api.get('/complaints/bugs/trends', { params }),
  create: (data: any) => api.post('/complaints', data),
  update: (id: string, data: any) => api.put(`/complaints/${id}`, data),
  // FEATURE (2026-09-02, Jaime): super-admin delete — DELETE /api/complaints/:id.
  // Backend enforces super_admin; UI hides the button for every other role.
  remove: (id: string) => api.delete(`/complaints/${id}`),
};

export const regulationsAPI = {
  getAll: (condoId: string) => api.get(`/regulations/condo/${condoId}`),
  create: (data: any) => api.post('/regulations', data),
  download: (id: string) => api.get(`/regulations/${id}/download`),
};

export const emergencyNumbersAPI = {
  getAll: (condoId: string) => api.get(`/emergency-numbers/condo/${condoId}`),
  create: (data: any) => api.post('/emergency-numbers', data),
  remove: (id: string) => api.delete(`/emergency-numbers/${id}`),
};

export const securityGuardsAPI = {
  getAll: (condoId: string) => api.get(`/security/condo/${condoId}/guards`),
  create: (data: any) => api.post('/security/guards', data),
  update: (id: string, data: any) => api.put(`/security/guards/${id}`, data),
};

export const securityCompaniesAPI = {
  getAll: (condoId: string) => api.get(`/security/condo/${condoId}/companies`),
  create: (data: any) => api.post('/security/companies', data),
};

export const notificationsAPI = {
  // The mounted backend route scopes by the authenticated req.userId and ignores
  // this path segment. Keep a stable sentinel instead of sending a URL user ID.
  getAll: (_userId?: string) => api.get('/notifications/user/me'),
  markAsRead: (id: string) => api.patch(`/notifications/${id}/read`),
  markAllAsRead: (_userId?: string) => api.patch('/notifications/user/me/read-all'),
};

export const monthlyClosuresAPI = {
  getAll: (condoId: string) => api.get(`/financial/condo/${condoId}/closures`),
  create: (condoId: string, data: { period_year: number; period_month: number }) =>
    api.post(`/financial/condo/${condoId}/close-month`, data),
};

export const pettyCashAPI = {
  getAll: (condoId: string) => api.get(`/expenses/condo/${condoId}/petty-cash`),
  create: (data: any) => api.post('/expenses/petty-cash', data),
};

export const condoInfoAPI = {
  get: (condoId: string) => api.get(`/condos/${condoId}/info`),
  update: (condoId: string, data: any) => api.put(`/condos/${condoId}/info`, data),
};

export const suppliersAPI = {
  getAll: (condoId: string) => api.get(`/businesses/condo/${condoId}/suppliers`),
  getMarketplaceDirectory: () => api.get('/suppliers/directory'),
  create: (data: any) => api.post('/suppliers', data),
  update: (id: string, data: any) => api.put(`/suppliers/${id}`, data),
  getDetail: (condoId: string, supplierId: string) =>
    api.get(`/businesses/condo/${condoId}/suppliers/${supplierId}`),
  getDashboard: (condoId: string) =>
    api.get(`/businesses/condo/${condoId}/suppliers/me/dashboard`),
  registerSupplier: (condoId: string, data: any) =>
    api.post(`/businesses/condo/${condoId}/suppliers`, data),
  // F97/F99 contracts are intentionally isolated here; backend availability is tracked in the spec.
  updateRequest: (condoId: string, requestId: string, action: 'accept' | 'reject' | 'complete' | 'propose-date', data?: any) =>
    api.post(`/businesses/condo/${condoId}/suppliers/me/requests/${requestId}/${action}`, data),
  getProviderPayments: (condoId: string) =>
    api.get(`/businesses/condo/${condoId}/suppliers/me/payments`),
};

export const condoMembersAPI = {
  getMembers: (condoId: string) => api.get(`/condos/${condoId}/members`),
  addMember: (condoId: string, data: any) => api.post(`/condos/${condoId}/members`, data),
  editMember: (condoId: string, memberId: string, data: any) =>
    api.patch(`/condos/${condoId}/members/${memberId}`, data),
  suspendMember: (condoId: string, memberId: string) =>
    api.patch(`/condos/${condoId}/members/${memberId}/suspend`),
};

export const projectsAPI = {
  getAll: (condoId: string) => api.get(`/financial/condo/${condoId}/projects`),
  create: (condoId: string, data: any) => api.post(`/financial/condo/${condoId}/projects`, data),
  getById: (condoId: string, id: string) => api.get(`/financial/condo/${condoId}/projects/${id}`),
  addQuote: (condoId: string, projectId: string, data: any) =>
    api.post(`/financial/condo/${condoId}/projects/${projectId}/quotes`, data),
  updateQuoteStatus: (condoId: string, projectId: string, quoteId: string, action: string) => {
    if (action !== 'approve') return Promise.reject(new Error('Project quote action is not supported by the API'));
    return api.put(`/financial/condo/${condoId}/projects/${projectId}/quotes/${quoteId}/approve`);
  },
  updateStatus: (condoId: string, projectId: string, action: string) => {
    if (action !== 'approve') return Promise.reject(new Error('Project status action is not supported by the API'));
    return api.post(`/financial/condo/${condoId}/projects/${projectId}/approve`);
  },
};

export { api };
export default api;
